import React from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';
import { connect } from 'react-redux';

import Lozenge from '@atlaskit/lozenge';

import { FabricUser } from 'src/components/bbc-atlaskit-editor';
import { toFabricUser } from 'src/components/conversation-provider/utils';
import { Task } from 'src/components/types';
import { FabricUserAvatar } from 'src/containers/fabric-user-avatar';
import { FabricUserName } from 'src/containers/fabric-user-name';
import {
  taskStateChange,
  TASK_DELETE,
  TOGGLE_EDIT_COMMENT_TASK_INPUT,
} from 'src/redux/pull-request/actions';
import {
  getCurrentPullRequestId,
  getIsCurrentPullRequestOpen,
} from 'src/redux/pull-request/selectors';
import { getIsReviewInProgress } from 'src/redux/pull-request/selectors/review-selectors';
import {
  getTaskLocation,
  TaskMap,
  EditableTaskState,
} from 'src/redux/pull-request/tasks-reducer';
import { TaskStateChangeOptions } from 'src/redux/pull-request/types';
import { getCurrentRepositoryUuid } from 'src/selectors/repository-selectors';
import {
  getTasksForComment,
  getCanResolveTask,
  isCreateTaskInputVisible,
  getTasksSlice,
  getModifiableTasks,
} from 'src/selectors/task-selectors';
import { BucketState, BucketDispatch } from 'src/types/state';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { CreateCommentTask } from './create-comment-task';
import { CreateTaskKind } from './create-task';
import { EditCommentTask } from './edit-comment-task';
import TaskComponent from './task';
import * as taskStyle from './task-list.style';
import * as style from './tasks.style';
import { taskKey } from './utils';

type OwnProps = {
  /* eslint react/no-unused-prop-types: warn */
  intl: InjectedIntl;
  /** Define this field for comment tasks. It will be used as an arg to fetch comment tasks from Redux if 'taskList' is not defined.
   * This field will also be used to determine task location and allow comment tasks to be created inline if defined. This should be undefined for global tasks. */
  commentId?: string;
  /** If taskList is provided, it will be used instead of fetching tasks from the store. If this is undefined, tasks will be fetched from Redux
   * using the 'commentId' prop. Global tasks should always provide a 'taskList' because no tasks will be fetched with an undefined 'commentId'. */
  taskList?: Task[];
  /** If true, will render the avatar and username of each task creator */
  renderAvatar?: boolean;
};

type TaskListProps = OwnProps & {
  tasks: Task[];
  modifiableTaskKeys: string[];
  canResolveTask?: boolean;
  onTaskStateChange: (props: TaskStateChangeOptions) => void;
  onTaskDelete: (task: Task) => void;
  onTaskEditBegin: (task: Task) => void;
  isCreateTaskInputVisible: boolean;
  createTaskHasError: boolean;
  createTaskErrorMessage: string;
  isCreateTaskSubmitting: boolean;
  isDeleteTaskSubmitting: TaskMap<boolean>;
  commentEditingTasks: TaskMap<EditableTaskState>;
  pullRequestId: number | null;
  repositoryUUID: string | null;
  isReviewInProgress: boolean;
};

const mapStateToProps = (state: BucketState, ownProps: OwnProps) => {
  const {
    createTaskHasErrorByTaskKey,
    createTaskErrorMessageByTaskKey,
    isCreateTaskSubmittingByTaskKey,
    isDeleteTaskSubmitting,
    commentEditingTasks,
  } = getTasksSlice(state);
  const { commentId, taskList } = ownProps;
  const taskLocation = getTaskLocation({ isGlobal: !commentId, commentId });
  const isOpenPr = getIsCurrentPullRequestOpen(state);

  let tasks: Task[] = [];
  if (taskList) tasks = taskList;
  else if (commentId) tasks = getTasksForComment(state, commentId);

  return {
    repositoryUUID: getCurrentRepositoryUuid(state),
    pullRequestId: getCurrentPullRequestId(state),
    createTaskHasError: createTaskHasErrorByTaskKey[taskLocation],
    createTaskErrorMessage: createTaskErrorMessageByTaskKey[taskLocation],
    isCreateTaskSubmitting: isCreateTaskSubmittingByTaskKey[taskLocation],
    canResolveTask: getCanResolveTask(state) && isOpenPr,
    modifiableTaskKeys: getModifiableTasks(state),
    tasks,
    isCreateTaskInputVisible: commentId
      ? isCreateTaskInputVisible(state, commentId) && isOpenPr
      : false,
    isDeleteTaskSubmitting,
    commentEditingTasks,
    isReviewInProgress: getIsReviewInProgress(state),
  };
};

const mapDispatchToProps = (dispatch: BucketDispatch) => ({
  onTaskStateChange: (props: TaskStateChangeOptions) =>
    dispatch(taskStateChange(props)),
  onTaskDelete: (task: Task) =>
    dispatch({
      type: TASK_DELETE.REQUEST,
      payload: task,
    }),
  onTaskEditBegin: (task: Task) =>
    dispatch({
      type: TOGGLE_EDIT_COMMENT_TASK_INPUT,
      payload: {
        taskId: task.id,
        isEditing: true,
      },
    }),
});

type AvatarWrapperProps = {
  avatarSettings?: {
    /**  User data of task creator */
    user: FabricUser;
    /** Renders pending lozenge if task is pending.
    Note: When the avatar is rendered, the pending lozenge will be rendered here.
    Otherwise, the pending lozenge will be rendered within 'TaskComponent' below*/
    isTaskPending: boolean;
  };
  children: React.ReactNode;
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const AvatarWrapper: React.FC<AvatarWrapperProps> = ({
  avatarSettings,
  children,
}) => {
  return avatarSettings ? (
    <style.TaskAvatarContainer>
      <FabricUserAvatar user={avatarSettings.user} />
      <style.TaskUsernameContainer>
        <style.TaskUsername>
          <FabricUserName user={avatarSettings.user} />
          {avatarSettings.isTaskPending && (
            <style.PendingLozengeContainer>
              <Lozenge appearance="new">PENDING</Lozenge>
            </style.PendingLozengeContainer>
          )}
        </style.TaskUsername>
        <style.TaskContent>{children}</style.TaskContent>
      </style.TaskUsernameContainer>
    </style.TaskAvatarContainer>
  ) : (
    <>{children}</>
  );
};

export class BaseTaskList extends React.PureComponent<TaskListProps> {
  handleTaskStateChange = (options: TaskStateChangeOptions) => {
    const { nextTaskState: state } = options;

    publishUiEvent({
      action: state === 'RESOLVED' ? 'resolved' : 'unresolved',
      actionSubject: 'task',
      actionSubjectId: 'pullRequestTask',
      source: 'pullRequestScreen',
      objectType: 'pullRequest',
      objectId: `${this.props.repositoryUUID}/${this.props.pullRequestId}`,
      containerId: `${this.props.repositoryUUID}`,
      containerType: 'repository',
      attributes: {
        taskLocation: 'comment',
      },
    });

    this.props.onTaskStateChange(options);
  };

  render() {
    const {
      tasks,
      onTaskDelete,
      onTaskEditBegin,
      modifiableTaskKeys,
      canResolveTask,
      isCreateTaskInputVisible: isTaskInputVisible,
      createTaskHasError,
      createTaskErrorMessage,
      isCreateTaskSubmitting,
      isDeleteTaskSubmitting,
      commentEditingTasks,
      commentId,
      renderAvatar,
      isReviewInProgress,
    } = this.props;
    return (
      <>
        {isTaskInputVisible && commentId && (
          <CreateCommentTask
            kind={CreateTaskKind.Comment}
            commentId={commentId}
            isTaskPending={isReviewInProgress}
            createTaskHasError={createTaskHasError}
            createTaskErrorMessage={createTaskErrorMessage}
            isCreateTaskSubmitting={isCreateTaskSubmitting}
          />
        )}
        {tasks && tasks.length ? (
          <taskStyle.taskList>
            {tasks.map((task: Task) => {
              const key = taskKey(task);
              const commentTaskEditState = commentEditingTasks[task.id];
              const isEditing =
                commentTaskEditState &&
                commentTaskEditState.state !== 'notEditing';
              const canModify = modifiableTaskKeys.includes(key);
              const user = toFabricUser(task.creator);
              const avatarSettings = renderAvatar
                ? { user, isTaskPending: task.pending ?? false }
                : undefined;

              return (
                <style.TaskContainer key={key}>
                  <AvatarWrapper avatarSettings={avatarSettings}>
                    {isEditing ? (
                      <EditCommentTask task={task} />
                    ) : (
                      <TaskComponent
                        key={key}
                        task={task}
                        isDeleting={isDeleteTaskSubmitting[key]}
                        onTaskDelete={canModify ? onTaskDelete : undefined}
                        onTaskEditBegin={
                          canModify ? onTaskEditBegin : undefined
                        }
                        canResolveTask={canResolveTask}
                        onTaskStateChange={this.handleTaskStateChange}
                        hidePendingStatus={renderAvatar}
                        rightAlignIcons={false}
                      />
                    )}
                  </AvatarWrapper>
                </style.TaskContainer>
              );
            })}
          </taskStyle.taskList>
        ) : null}
      </>
    );
  }
}

export const TaskList = injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(BaseTaskList)
);
